import { useEffect, useState } from "react";
import type { StorySummary, StoryView } from "../shared/story";
import { api } from "./api";
import { ClothThumb } from "./cloth";
import { Sheet } from "./trust";

/**
 * Your stories, from anywhere (owner, 2026-10-18: "how do I get out of a story and continue in a different one?"). A
 * sheet over the story you're in: begin a new one, or open any other, each with its cloth so far, how long it is and
 * when you last played it; the one you're in is marked. Opened by "Stories" in every theme's header and footer, or by
 * typing "stories". Stories save as you go, so leaving is always safe (mid-reply counts as cutting in). At the foot, the
 * front page (/welcome): weftwake.com takes anyone who has played straight to their stories (owner, 2026-10-20: "there's
 * no way to go to the homepage").
 */

/** Views fetched for the cloths: the most recent stories only (one request each). */
const CLOTHS_MAX = 12;

export function ago(ms: number, now = Date.now()): string {
  const m = Math.round((now - ms) / 60000);
  if (m < 2) return "just now";
  if (m < 60) return `${m} minutes ago`;
  const h = Math.round(m / 60);
  if (h < 24) return h === 1 ? "an hour ago" : `${h} hours ago`;
  const d = Math.round(h / 24);
  if (d === 1) return "yesterday";
  if (d < 7) return `${d} days ago`;
  return new Date(ms).toLocaleDateString(undefined, { day: "numeric", month: "long" });
}

export function StoriesSheet({ current, nav, onClose }: { current: string | null; nav(path: string): void; onClose(): void }) {
  const [list, setList] = useState<StorySummary[] | null>(null);
  const [views, setViews] = useState<Record<string, StoryView>>({});
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api.stories().then((r) => {
      const stories = [...r.stories].sort((a, b) => b.updatedAt - a.updatedAt);
      setList(stories);
      for (const st of stories.slice(0, CLOTHS_MAX)) {
        api.story(st.id).then((v) => setViews((cur) => ({ ...cur, [st.id]: v.story })), () => {});
      }
    }, () => setFailed(true));
  }, []);
  const open = (path: string) => { onClose(); nav(path); };
  return (
    <Sheet label="Your stories" onClose={onClose}>
      <div className="lm-stories">
        <header className="lm-stories-head">
          <h2 className="lm-sheet-h">Your stories</h2>
          <button type="button" className="lm-stories-new" onClick={() => open("/")}>Begin a new story</button>
        </header>
        <p className="lm-stories-note">Stories are saved as you go. Leave any time; you'll pick up where you left off.</p>
        {failed ? <p role="alert">Your stories wouldn't load. Check your connection and try again.</p>
          : !list ? <p className="lm-stories-note">…</p>
          : !list.length ? <p className="lm-stories-note">No stories yet.</p>
          : (
            <ul className="lm-stories-list">
              {list.map((st) => {
                const v = views[st.id];
                const here = st.id === current;
                return (
                  <li key={st.id}>
                    <button type="button" className="lm-stories-row" aria-current={here ? "page" : undefined} onClick={() => (here ? onClose() : open(`/s/${st.id}`))}>
                      {v ? <ClothThumb view={v} w={44} h={56} className="lm-stories-cloth" /> : <span className="lm-stories-cloth lm-stories-cloth-empty" aria-hidden="true" />}
                      <span className="lm-stories-text">
                        <strong>{st.title}</strong>
                        <span className="lm-map-meta">
                          {st.n} turn{st.n === 1 ? "" : "s"} · {here ? "you're in it" : `played ${ago(st.updatedAt)}`}{v?.ended ? " · ended" : ""}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        <footer className="lm-stories-foot">
          <a href="/welcome">Weftwake home</a>
          <button type="button" onClick={onClose}>Close</button>
        </footer>
      </div>
    </Sheet>
  );
}
